<template>
  <CustomDropdown
    :ref="(el) => setDropdownRef(el)"
    maxHeight="300px"
    gap="10px"
    position="end"
    dropdownCSS="p-0"
    :relative-to-viewport="true"
    :disabled="!hasSequenceAccess"
    @close="$emit('update:modelValue', '')"
  >
    <template #trigger>
      <div class="quick_action_item mt-1">
        <a-tooltip placement="top" :title="`Add to ${featureNames.SEQUENCES}`">
          <ThemeImage
            v-if="isSequenceEnabled"
            class="size-5"
            :icon="icon"
            alt="add_to_sequence"
          />
        </a-tooltip>
      </div>
    </template>

    <template #default>
      <div class="flex w-[266px] flex-col">
        <div class="flex flex-col max-h-[300px]">
          <div
            class="flex flex-col py-3 px-2"
            :class="{ border_bottom: sequences?.length }"
          >
            <span
              class="secondary_color font-semibold text-xs leading-[17px] px-3 pb-2"
            >
              CHOOSE A {{ featureNames.SEQUENCES.toUpperCase() }}
            </span>
            <CustomInput
              format="text"
              v-model="inputValue"
              placeholder="Search here"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              alt="search_icon"
              auto-focus
            />
          </div>

          <div
            v-if="sequences?.length"
            class="flex flex-col p-2 overflow-y-scroll custom_scrollbar"
          >
            <a-tooltip
              v-for="(sequence, index) in sequences"
              :key="`${sequence?.id}_sequence_${index}`"
              placement="top"
              :title="
                shouldShowTooltip(sequence.id + '_' + index)
                  ? sequence?.name || ''
                  : ''
              "
            >
              <div
                class="dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
                @click="handleSequence(sequence)"
              >
                <div
                  class="w-full flex flex-row items-center justify-between gap-1 text-sm"
                >
                  <div
                    class="truncate w-[65%]"
                    :ref="
                      (el) => setContainerRef(el, sequence.id + '_' + index)
                    "
                  >
                    <span
                      :ref="(el) => setTextRef(el, sequence.id + '_' + index)"
                      class="text_color truncate text-sm font-normal leading-[17px]"
                      >{{ sequence?.name || "" }}</span
                    >
                  </div>
                  <div
                    :class="[
                      'px-2 flex items-center  justify-center py-0 rounded-full',
                      sequence?.status === 'Active'
                        ? 'active_sequence_tag'
                        : 'inactive_sequence_tag',
                    ]"
                  >
                    <span
                      :class="[
                        sequence?.status === 'Active'
                          ? 'text-white'
                          : 'text_color',
                        'text-[11px] font-semibold',
                      ]"
                    >
                      {{
                        sequence?.status === "Active" ? "Active" : "Inactive"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </a-tooltip>
          </div>

          <div class="flex flex-col p-2 border_top">
            <div
              class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
              @click="handleSequenceCreate"
            >
              + Create New {{ featureNames.SEQUENCES }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { computed, ref } from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  row: { type: Object, required: true },
  sequences: { type: Array, default: () => [] },
  modelValue: { type: String, required: true },
  isSequenceEnabled: { type: Boolean, default: false },
  hasSequenceAccess: { type: Boolean, default: true },
});

const emit = defineEmits([
  "sequence-created",
  "sequence-create",
  "update:modelValue",
]);

const dropdownRef = ref(null);
const icon = imageNames.ADD_TO_SEQUENCE_GREY;

const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();

const inputValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const setDropdownRef = (el) => {
  dropdownRef.value = el;
};

const closeDropdown = () => {
  emit("update:modelValue", "");
  dropdownRef.value?.closeDropdown();
};

const handleSequence = (sequence) => {
  const sequenceId = typeof sequence === 'object' ? sequence.id : sequence;
  const sequenceName = typeof sequence === 'object' ? sequence.name : null;
  emit("sequence-created", { sequenceId, sequenceName, rowId: props.row.id });
  closeDropdown();
};

const handleSequenceCreate = () => {
  emit("sequence-create", props.row);
  closeDropdown();
};
</script>
